import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ToasterType = 'success' | 'error' | 'warning' | 'info';

export interface ToasterMessage {
  id: string;
  type: ToasterType;
  message: string;
  duration?: number;
  /** When false, the progress bar is hidden (e.g. during file upload). Default true when duration is set. */
  showProgress?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ToasterService {
  private messagesSubject = new BehaviorSubject<ToasterMessage[]>([]);
  public messages$: Observable<ToasterMessage[]> = this.messagesSubject.asObservable();

  constructor(private ngZone: NgZone) {}

  /**
   * Show a toaster message
   * @param type - Type of message (success, error, warning, info)
   * @param message - Message to display
   * @param duration - Duration in milliseconds (default: 3000)
   * @param showProgress - When false, hides the progress bar (default: true)
   */
  show(type: ToasterType, message: string, duration: number = 3000, showProgress: boolean = true): void {
    const id = this.generateId();
    const toaster: ToasterMessage = { id, type, message, duration, showProgress };

    // Add to messages array
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, toaster]);

    // Auto-dismiss after duration - run inside Angular zone
    if (duration > 0) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.ngZone.run(() => {
            this.dismiss(id);
          });
        }, duration);
      });
    }
  }

  /**
   * Show success toaster
   */
  success(message: string, duration: number = 3000, showProgress: boolean = true): void {
    this.show('success', message, duration, showProgress);
  }

  /**
   * Show error toaster
   */
  error(message: string, duration: number = 3000, showProgress: boolean = true): void {
    this.show('error', message, duration, showProgress);
  }

  /**
   * Show warning toaster
   */
  warning(message: string, duration: number = 3000): void {
    this.show('warning', message, duration);
  }

  /**
   * Show info toaster
   */
  info(message: string, duration: number = 3000): void {
    this.show('info', message, duration);
  }

  /**
   * Dismiss a specific toaster by id
   */
  dismiss(id: string): void {
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next(currentMessages.filter((msg) => msg.id !== id));
  }

  /**
   * Clear all toasters
   */
  clear(): void {
    this.messagesSubject.next([]);
  }

  /**
   * Generate unique ID for toaster
   */
  private generateId(): string {
    return `toaster-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

